import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { FormulaListActions } from './formula-list.actions';
import { DEFAULT_SCRIPT } from '../../features/formula/constants/utils';
import { Formula, FormulaVariable } from '../../features/formula/models/formula.model';
import { FormulaService } from '../../features/formula/services/formula.service';
import { SemerisRoutes } from '../../features/shared/constants/routes';
import { WebSocketEntities } from '../../features/shared/models/web-socket/web-socket.models';
import { createWebSocketEffect } from '../../features/shared/services/web-socket/create-web-socket-effect';
import { WebSocketService } from '../../features/shared/services/web-socket/web-socket.service';
import {
  createNotificationEffect,
  PushNotificationRecord,
} from '../../features/shared/utils/create-notification-effect';
import { reportEffectError } from '../../features/shared/utils/report-effect-error';
import { AppActions } from '../app/app.actions';
import { RootState } from '../root-state.model';

@Injectable()
export class FormulaListEffects {
  private service = inject(FormulaService);
  private actions$ = inject(Actions);
  private readonly store = inject<Store<RootState>>(Store);
  private readonly router = inject(Router);
  private readonly webSocketService = inject(WebSocketService);

  private readonly notifications: PushNotificationRecord = {
    [FormulaListActions.LoadListFail.type]: {
      message: 'Failed to load the formulas',
      isSuccess: false,
    },
    [FormulaListActions.DeleteFormulaFail.type]: {
      message: 'Failed to delete the formula',
      isSuccess: false,
    },
    [FormulaListActions.DeleteFormulaSuccess.type]: { message: 'Formula deleted', isSuccess: true },
    [FormulaListActions.SaveFormulaDetailsSuccess.type]: {
      message: 'Formula saved',
      isSuccess: true,
    },
    [FormulaListActions.SaveFormulaDetailsFail.type]: {
      message: 'Failed to save the formula',
      isSuccess: false,
    },
  };

  loadListEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaListActions.LoadList),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.getAll().pipe(
          map((list) => FormulaListActions.LoadListSuccess({ list })),
          reportEffectError(FormulaListActions.LoadListFail)
        )
      )
    )
  );

  deleteFormulaEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaListActions.DeleteFormula),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service.delete(action.id).pipe(
          map(() => FormulaListActions.DeleteFormulaSuccess({ id: action.id })),
          catchError((error) => {
            console.error(error);
            return of(FormulaListActions.DeleteFormulaFail({ error }));
          })
        )
      )
    )
  );

  createModalClosedEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.CreateModalClosed),
      filter((action) => action.displayName && action.modalId === 'formula'),
      map((action) => FormulaListActions.CreateFormula({ name: action.displayName }))
    )
  );

  createFormulaEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaListActions.CreateFormula),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service
          .create({
            displayName: action.name,
            description: action.description,
            script: DEFAULT_SCRIPT,
            variables: [],
          })
          .pipe(
            map((formula) => FormulaListActions.CreateFormulaSuccess({ formula })),
            reportEffectError(FormulaListActions.CreateFormulaFail)
          )
      )
    )
  );

  createFormulaSuccessEffect$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FormulaListActions.CreateFormulaSuccess),
        tap((action) => {
          this.router.navigate(['/', SemerisRoutes.formula.root, (action as any).formula.id]);
        })
      ),
    { dispatch: false }
  );

  notificationEffect$: Observable<Action> = createNotificationEffect(
    this.actions$,
    this.notifications
  );

  wsEffect$: Observable<Action> = createWebSocketEffect(
    this.store,
    this.actions$,
    this.webSocketService,
    WebSocketEntities.Formula,
    () => FormulaListActions.LoadList()
  );

  templateModalClosed$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(AppActions.TemplateModalClosed),
      filter((action) => action.data && action.modalId === 'edit-formula-list'),
      withLatestFrom(this.store),
      map(([action, store]) =>
        FormulaListActions.SaveFormulaDetails({
          formula: {
            ...store.formula.list?.find((f) => f.id === action?.data?.id),
            ...action.data,
            variables: (action.additionalInfo as { variables: FormulaVariable[] })?.variables,
          } as Formula,
        })
      )
    )
  );

  updateFormulaEffect$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(FormulaListActions.SaveFormulaDetails),
      withLatestFrom(this.store),
      switchMap(([action, store]) =>
        this.service
          .update(action.formula, true, !!action.formula?.canShare, !!action.formula?.canPublish)
          .pipe(
            map((formula) => FormulaListActions.SaveFormulaDetailsSuccess({ formula })),
            catchError((error: HttpErrorResponse) =>
              of(FormulaListActions.SaveFormulaDetailsFail({ error }))
            )
          )
      )
    )
  );
}
